import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ArrowUpRight, Bell } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '~/api/trpc';
import { AddTreatmentDialog } from '~/components/medical-record';
import OpenBillingDraftStatusBadge, { type OpenBillingDraftEntry } from '~/components/qm/OpenBillingDraftStatusBadge';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { formatCurrency } from '~/lib/utils';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type OutstandingEntry = {
    appointmentId: string;
    dateFrom: string;
    storeName: string;
    customerId: number;
    customerName: string;
    documentationStatus: 'NONE' | 'ATTRIBUTION_ONLY';
    treatmentId: string | null;
};

type DocDialogState = {
    mode: 'new' | 'edit';
    customerId: number;
    appointmentId: string;
    treatmentId: string | null;
};

type BellTab = 'documentation' | 'billing-drafts';

const formatCount = (count: number) => (count > 99 ? '99+' : count);

const CountPill = ({ count }: { count: number }) => {
    if (count === 0) return null;
    return (
        <span className='ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-medium text-white'>
            {formatCount(count)}
        </span>
    );
};

const onActivateKey = (event: KeyboardEvent<HTMLDivElement>, action: () => void) => {
    if (event.target !== event.currentTarget) {
        return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
    }
};

const listItemClassName = 'flex w-full cursor-pointer flex-col items-start gap-1 border-b px-4 py-3 text-left last:border-b-0 hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/50';

const CustomerLink = ({ customerId, customerName }: { customerId: number; customerName: string }) => (
    <a
        href={`/customer/${customerId}`}
        target='_blank'
        rel='noopener noreferrer'
        className='inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2 hover:text-primary'
        onClick={(event) => event.stopPropagation()}
    >
        {customerName}
        <ArrowUpRight className='h-3.5 w-3.5' />
    </a>
);

const OutstandingDocumentationBell = () => {
    const practitionerId = useUserStore((state) => state.user?.practitionerId ?? null);
    const role = useUserStore((state) => state.user?.role);
    const navigate = useNavigate();
    const trpcUtils = trpc.useUtils();
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<BellTab>('documentation');
    const [docDialog, setDocDialog] = useState<DocDialogState | null>(null);

    const canSeeDocs = practitionerId !== null;
    const canSeeDrafts = role === UserRole.ADMIN || role === UserRole.OPERATOR;

    const { data: docData, isLoading: docsLoading } = trpc.qm.getMyOutstandingDocumentation.useQuery(undefined, {
        enabled: canSeeDocs,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
    });

    const { data: draftData, isLoading: draftsLoading } = trpc.qm.getMyOpenBillingDrafts.useQuery(undefined, {
        enabled: canSeeDrafts,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
    });

    if (!canSeeDocs && !canSeeDrafts) {
        return null;
    }

    const docEntries: OutstandingEntry[] = canSeeDocs ? docData?.entries ?? [] : [];
    const draftEntries: OpenBillingDraftEntry[] = canSeeDrafts
        ? (draftData?.entries as OpenBillingDraftEntry[] | undefined) ?? []
        : [];
    const totalCount = docEntries.length + draftEntries.length;

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            if (canSeeDocs && docEntries.length > 0) {
                setActiveTab('documentation');
            } else if (canSeeDrafts && draftEntries.length > 0) {
                setActiveTab('billing-drafts');
            } else {
                setActiveTab(canSeeDocs ? 'documentation' : 'billing-drafts');
            }
        }
        setOpen(nextOpen);
    };

    const handleSelectDoc = (entry: OutstandingEntry) => {
        setOpen(false);
        if (entry.documentationStatus === 'ATTRIBUTION_ONLY' && entry.treatmentId) {
            setDocDialog({
                mode: 'edit',
                customerId: entry.customerId,
                appointmentId: entry.appointmentId,
                treatmentId: entry.treatmentId,
            });
            return;
        }
        setDocDialog({
            mode: 'new',
            customerId: entry.customerId,
            appointmentId: entry.appointmentId,
            treatmentId: null,
        });
    };

    const handleSelectDraft = (entry: OpenBillingDraftEntry) => {
        setOpen(false);
        navigate(`/billing-draft/${entry.billingDraftId}`);
    };

    const docList = (
        <div className='max-h-96 overflow-y-auto'>
            {docsLoading ? (
                <p className='px-4 py-6 text-sm text-muted-foreground'>Laden...</p>
            ) : docEntries.length === 0 ? (
                <p className='px-4 py-6 text-sm text-muted-foreground'>Keine offenen Dokumentationen</p>
            ) : (
                docEntries.map((entry) => (
                    <div
                        key={entry.appointmentId}
                        role='button'
                        tabIndex={0}
                        className={listItemClassName}
                        onClick={() => handleSelectDoc(entry)}
                        onKeyDown={(event) => onActivateKey(event, () => handleSelectDoc(entry))}
                    >
                        <CustomerLink customerId={entry.customerId} customerName={entry.customerName} />
                        <span className='text-xs text-muted-foreground'>
                            {format(new Date(entry.dateFrom), 'dd.MM.yyyy HH:mm', { locale: de })} · {entry.storeName}
                        </span>
                        {entry.documentationStatus === 'NONE' ? (
                            <Badge variant='outline' className='bg-red-100 text-red-800 border-red-200'>
                                Keine Dokumentation
                            </Badge>
                        ) : (
                            <Badge variant='outline' className='bg-amber-100 text-amber-800 border-amber-200'>
                                Nur Leistung dokumentiert
                            </Badge>
                        )}
                    </div>
                ))
            )}
        </div>
    );

    const draftList = (
        <div className='max-h-96 overflow-y-auto'>
            {draftsLoading ? (
                <p className='px-4 py-6 text-sm text-muted-foreground'>Laden...</p>
            ) : draftEntries.length === 0 ? (
                <p className='px-4 py-6 text-sm text-muted-foreground'>Keine offenen Aufträge</p>
            ) : (
                draftEntries.map((entry) => (
                    <div
                        key={entry.billingDraftId}
                        role='button'
                        tabIndex={0}
                        className={listItemClassName}
                        onClick={() => handleSelectDraft(entry)}
                        onKeyDown={(event) => onActivateKey(event, () => handleSelectDraft(entry))}
                    >
                        <CustomerLink customerId={entry.customerId} customerName={entry.customerName} />
                        <span className='text-xs text-muted-foreground'>
                            {entry.billingDraftNumber} · {format(new Date(entry.billingDraftDate), 'dd.MM.yyyy', { locale: de })} · {entry.storeName}
                        </span>
                        <div className='flex items-center gap-2'>
                            <OpenBillingDraftStatusBadge status={entry.paymentStatus} />
                            <span className='text-xs text-muted-foreground'>
                                bezahlt {formatCurrency(entry.paidCents / 100)} / {formatCurrency(entry.totalCents / 100)}
                            </span>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const showTabs = canSeeDocs && canSeeDrafts;

    return (
        <>
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <Button variant='ghost' size='icon' className='relative' aria-label='Benachrichtigungen'>
                        <Bell className='h-5 w-5' />
                        {totalCount > 0 && (
                            <span className='absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-medium text-white'>
                                {formatCount(totalCount)}
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent align='end' className='w-96 p-0'>
                    {showTabs ? (
                        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as BellTab)}>
                            <div className='border-b px-2 py-2'>
                                <TabsList className='w-full'>
                                    <TabsTrigger value='documentation' className='flex-1'>
                                        Offene Dokumentation
                                        <CountPill count={docEntries.length} />
                                    </TabsTrigger>
                                    <TabsTrigger value='billing-drafts' className='flex-1'>
                                        Offene Aufträge
                                        <CountPill count={draftEntries.length} />
                                    </TabsTrigger>
                                </TabsList>
                            </div>
                            <TabsContent value='documentation' className='mt-0'>
                                {docList}
                            </TabsContent>
                            <TabsContent value='billing-drafts' className='mt-0'>
                                {draftList}
                            </TabsContent>
                        </Tabs>
                    ) : (
                        <>
                            <div className='border-b px-4 py-3'>
                                <p className='text-sm font-medium'>
                                    {canSeeDocs ? 'Offene Dokumentation' : 'Offene Aufträge'}
                                </p>
                            </div>
                            {canSeeDocs ? docList : draftList}
                        </>
                    )}
                </PopoverContent>
            </Popover>

            {docDialog !== null && (
                <AddTreatmentDialog
                    customerId={docDialog.customerId}
                    forceNew={docDialog.mode === 'new'}
                    preselectedAppointmentId={docDialog.mode === 'new' ? docDialog.appointmentId : undefined}
                    treatmentId={docDialog.mode === 'edit' ? docDialog.treatmentId : undefined}
                    open
                    onOpenChange={(nextOpen) => {
                        if (!nextOpen) {
                            setDocDialog(null);
                        }
                    }}
                    onSuccess={() => {
                        setDocDialog(null);
                        void trpcUtils.qm.getMyOutstandingDocumentation.invalidate();
                    }}
                />
            )}
        </>
    );
};

export default OutstandingDocumentationBell;
